import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { DetectedLanguage, langMissmatch, TypedValidationError } from "./expiringFiles"
import { formatFileSize } from "./FileDropzone"
import Alert from "@mui/material/Alert"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import ErrorWithPopup from "./ErrorWithPopup"
import TrashIcon from "../../../components/icons/TrashIcon"
import { Severity } from "./FileUploadItemTooltip"
import { useResponsive } from "src/utils/useResponsive"

const textColor = (severity: Severity) => {
  switch (severity) {
    case "error":
      return "#EC5351"
    case "warning":
      return "#EE9C3C"
    case "info":
      return "#555657"
  }
}

export interface FileDetails {
  id: string
  size: number
  name: string
  typedValidationError?: TypedValidationError
  detectedLanguages?: DetectedLanguage[]
}

interface FileDropzoneItemProps {
  fileDetails: FileDetails
  expectedLanguage?: string
  onFileRemove: (fileId: string) => void
}

const FileDropzoneItem = ({ fileDetails, onFileRemove, expectedLanguage }: FileDropzoneItemProps) => {
  const { id, size, name, typedValidationError, detectedLanguages } = fileDetails
  const { t } = useTranslation("filesUpload")
  const [isRemoveFileHovered, setIsRemoveFileHovered] = useState(false)
  const { isMobile } = useResponsive()
  const handleFileRemove = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onFileRemove(id)
  }

  const languageMissmatch = langMissmatch(expectedLanguage, detectedLanguages)
  const severity = typedValidationError ? "error" : languageMissmatch ? "warning" : "info"
  const validationError = typedValidationError
    ? typedValidationError
    : languageMissmatch
    ? ({
        description: t("verifySourceLanguageHeader"),
        message: t("verifySourceLanguageMessage")
      } as TypedValidationError)
    : null

  return (
    <>
      <Alert
        severity={severity}
        icon={isMobile ? false : undefined}
        sx={{
          boxSizing: "border-box",
          width: "100%",
          boxShadow: isRemoveFileHovered ? "2px 2px 10px #0000001A" : "none",
          border: isRemoveFileHovered ? "1px solid #CCCCCC" : "1px solid #E7E7E7",
          "&.MuiAlert-root": {
            borderRadius: "30px"
          },
          "& .MuiAlert-message": {
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflowX: "hidden",
            maxWidth: "calc(100vw - 180px)"
          },
          "& .MuiAlert-action": {
            padding: "0px 0px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }
        }}
        action={
          <>
            {validationError && !isMobile && <ErrorWithPopup severity={severity} validationError={validationError} />}
            <Typography
              sx={{
                whiteSpace: "nowrap",
                px: 1,
                font: "inherit",
                color: "inherit"
              }}
            >
              {formatFileSize(size)}
            </Typography>
            <IconButton
              onClick={handleFileRemove}
              onMouseEnter={() => setIsRemoveFileHovered(true)}
              onMouseLeave={() => setIsRemoveFileHovered(false)}
              sx={{
                p: 0,
                color: textColor(severity),
                "&:hover": {
                  backgroundColor: "whitesmoke",
                  borderRadius: "20px",
                  color: textColor(severity)
                }
              }}
            >
              {<TrashIcon />}
            </IconButton>
          </>
        }
      >
        {name}
      </Alert>
    </>
  )
}

export default FileDropzoneItem
